import { useEffect, useMemo, useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Box, IconButton, ListSubheader, MenuItem, SxProps, Button } from '@mui/material'
import { Company } from '@/@data'
import type { CompanyStatusEnum, ICompany } from '@/@data'
import { useRouteQuery, useTranslator, useDebounce } from '@/hooks'
import { InputField } from '../common'
import { CreateEmployerDrawer } from '../employer/CreateEmployerDrawer'
import { _ } from 'numeral'

interface CompanySelectionProps {
  value?: string | string[] | number | number[]
  multiple?: boolean
  onChange?: (value: string, selected?: ICompany) => void
  disabled?: boolean
  required?: boolean
  label?: string
  placeholder?: string
  className?: string
  fullWidth?: boolean
  onClear?: () => void
  height?: string | number
  error?: boolean
  helperText?: string
  sx?: SxProps
  status?: CompanyStatusEnum
  agent_id?: number
  showEndAdornment?: boolean
}

const CompanySelection = ({
  value = '',
  multiple = false,
  onChange,
  onClear,
  disabled = false,
  label = '',
  placeholder = '',
  error = false,
  helperText = '',
  status,
  agent_id,
  required = false,
  showEndAdornment = true
}: CompanySelectionProps) => {
  const { serializeQuery } = useRouteQuery()
  const [search, setSearch] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const { __ } = useTranslator()
  const searchInputRef = useRef<HTMLInputElement>(null)

  const debouncedSearch = useDebounce(search, 500)

  const { data } = useQuery({
    ...Company.objects.paginateQueryFactory(
      serializeQuery({
        keywords: debouncedSearch,
        agent_id,
        status
      })
    )
  })

  const { data: selectedBranches } = useQuery({
    ...Company.objects.paginateQueryFactory(
      serializeQuery({
        ids: Array.isArray(value) ? value : [value]
      })
    ),
    enabled: !!value && (Array.isArray(value) ? value.length > 0 : true)
  })

  useEffect(() => {
    if (data) {
      setTimeout(() => searchInputRef.current?.focus())
    }
  }, [data])

  const options = useMemo(() => {
    const paginateOptions = data ? Company.getCompaniesSelectOptions(data.data) : []
    const selectedOptions = selectedBranches ? Company.getCompaniesSelectOptions(selectedBranches.data) : []

    const combinedOptions = [...paginateOptions]

    selectedOptions.forEach(option => {
      if (!combinedOptions.some(opt => opt.value === option.value)) {
        combinedOptions.push(option)
      }
    })

    return combinedOptions
  }, [data, selectedBranches])

  const handleCreateCompany = () => {
    setIsCreateModalOpen(true)
  }

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false)
    // Optionally trigger a refetch of companies or update the parent component
  }

  return (
    <>
      <InputField
        select
        value={value || (multiple ? [] : '')}
        label={
          required ? (
            <>
              {label}
              <span style={{ color: 'red' }}>*</span>
            </>
          ) : (
            label
          )
        }
        onChange={e => {
          onChange?.(
            e.target.value,
            data?.data.find(b => b.id === Number(e.target.value))
          )
        }}
        fullWidth
        error={error}
        helperText={helperText}
        disabled={disabled}
        slotProps={{
          input: {
            endAdornment:
              showEndAdornment &&
              onClear &&
              value &&
              ((multiple && Array.isArray(value) && value.length > 0) || (!multiple && value)) ? (
                <Box mr={5}>
                  <IconButton
                    size='small'
                    onClick={() => {
                      onClear?.()
                    }}
                    disabled={disabled}
                    aria-label='clear selection'
                    tabIndex={-1}
                  >
                    <i className='tabler-x text-[16px] text-textPrimary'></i>
                  </IconButton>
                </Box>
              ) : null
          },
          select: {
            multiple,
            displayEmpty: true,
            renderValue: (selected: any) => {
              if (!selected || (Array.isArray(selected) && selected.length === 0)) {
                return <span style={{ color: '#aaa' }}>{placeholder || label || __('Select company')}</span>
              }

              if (multiple && Array.isArray(selected)) {
                const selectedLabels = options
                  .filter(option => selected.map(Number).includes(Number(option.value)))
                  .map(option => option.label)
                  .join(', ')

                return <>{selectedLabels}</>
              }

              const found = options.find(option => Number(option.value) === Number(selected))

              return <>{found ? found.label : selected}</>
            }
          }
        }}
      >
        <ListSubheader onKeyDown={e => e.stopPropagation()}>
          <InputField
            fullWidth
            defaultValue={search}
            placeholder={__('Search Company')}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.stopPropagation()}
            inputRef={searchInputRef}
          />
        </ListSubheader>
        {options.map(option => (
          <MenuItem onKeyDown={e => e.stopPropagation()} key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
        <MenuItem onKeyDown={e => e.stopPropagation()} onClick={handleCreateCompany}>
          <Button variant='text' className='pa-0 ma-0' startIcon={<i className='tabler-circle-plus' />}>
            {__('Create Company')}
          </Button>
        </MenuItem>
      </InputField>

      <CreateEmployerDrawer open={isCreateModalOpen} setOpen={setIsCreateModalOpen} onSuccess={handleCreateSuccess} />
    </>
  )
}

export default CompanySelection
